import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AgencyAppState } from 'src/redux/modules/store';
import ContentHeader from '@common/component/organisms/ContentHeader';
import Layout from '../Layout';
import LinkedDataIcon from '@common/component/atoms/LinkedDataIcon';
import SearchConditionInput from '@common/container/molecules/SearchConditionInputContainer';
import SummmaryListComponent from '@common/component/molecules/SummaryList';
import CircularProgress from '@common/component/atoms/CircularProgress';
import { DEFAULT_PAGE_VIEW, SCREEN, SORT_TYPE, DEFAULT_SCROLL_INFO } from '@common/Common';
import Button from '@common/component/atoms/Button';
import LinkedDataResultTable from '@common/component/organisms/LinkedDataResultTable';
import Pagenation from '@common/component/molecules/Pagenation';
import FilterLinkedDataDrawer from '@common/component/organisms/FilterLinkedDataDrawer';
import LinkedDataStoreSelectModal from '@common/component/organisms/LinkedDataStoreSelectModal';
import StoreSelectModal from '../../../container/organisms/StoreSelectModalContainer';
import { actions } from '@common/redux/modules/Actions';
import { LinkedDataApiRequest, CooperationInfoList } from '@common/external/LinkedDataApi';
import { AppState } from '@common/redux/modules/store';
import {
  renderTitleSummary,
  renderLabelSummary,
  getViewList,
  renderAcceptedDatetime,
  convertDataFilterToRequest,
  renderStoreName,
  labelButtonSort,
  renderTextLinkedDataTd,
  scrollToTop,
  areCrewCodeAndNameMissing,
} from '@common/utils/LinkedDataUtils';
import { FilterConditionLinkedDataState, FilterLinkedDataStateBase } from '@common/redux/reducers/FilterLinkedDataReducer';
import { updateLinkedDataResult, updateRefreshPaginationKey } from '@common/redux/operations/LinkedDataResult';

const LinkedDataResult: React.FC = () => {
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { selectedStores } = useSelector((state: AppState) => state.storeSelector);
  const filterLinkedData = useSelector((state: AppState) => state.filterLinkedData);
  const { totalRecords, summary, cooperationInfoList, prevCooperationInfoList } = useSelector((state: AppState) => state.linkedDataResult);
  const { order, page, apiUpdateIndex } = filterLinkedData;
  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);
  const loginUser = useSelector((state: AgencyAppState) => state.loginUser);
  const fetchLoadingStatus = useSelector((state: AgencyAppState) => state.loadingStatus.filterLinkedData);

  const searchLinkRegistration = async (filterData: FilterConditionLinkedDataState) => {
    const convertData: FilterLinkedDataStateBase = {
      ...filterData,
      order,
      from: DEFAULT_SCROLL_INFO.FROM,
      apiUpdateIndex: DEFAULT_PAGE_VIEW.API_UPDATE_INDEX,
    };
    const request: LinkedDataApiRequest = convertDataFilterToRequest(selectedStores, convertData);
    updateLinkedDataResult(request, dispatch);
  };

  const searchLinkedDataByPage: CooperationInfoList[] = useMemo(() => {
    return getViewList(cooperationInfoList, page, apiUpdateIndex);
  }, [page, apiUpdateIndex, cooperationInfoList]);

  const handleSearchWhenChangeFromValue = (fromValue: number) => {
    dispatch(actions.filterLinkedData.updateScrollInfo(fromValue));

    const request: LinkedDataApiRequest = convertDataFilterToRequest(selectedStores, {
      ...filterLinkedData,
      from: fromValue,
    });
    updateLinkedDataResult(request, dispatch);
  };

  const handleClickSortOrder = (): void => {
    const orderValue = order === SORT_TYPE.CREATE_DATE_DESC ? SORT_TYPE.CREATE_DATE_ASC : SORT_TYPE.CREATE_DATE_DESC;
    dispatch(actions.filterLinkedData.updateSortInfo(orderValue));

    const request: LinkedDataApiRequest = convertDataFilterToRequest(selectedStores, {
      ...filterLinkedData,
      order: orderValue,
    });
    updateLinkedDataResult(request, dispatch);
  };

  const handleCloseFilterLinkedDataDrawer = (): void => {
    setIsOpenDrawer(false);
  };

  useEffect(() => {
    const request: LinkedDataApiRequest = convertDataFilterToRequest(selectedStores, filterLinkedData);
    updateLinkedDataResult(request, dispatch);
  }, [selectedStores]); // eslint-disable-line

  useEffect(() => {
    const fromValue = updateRefreshPaginationKey(prevCooperationInfoList, cooperationInfoList, totalRecords, page, dispatch);
    if (fromValue === -1) return;
    // Handle case when totalRecord greater than 0 and cooperationInfoList empty
    handleSearchWhenChangeFromValue(fromValue);
  }, [cooperationInfoList, prevCooperationInfoList, totalRecords, page, dispatch]); // eslint-disable-line

  return (
    <Layout isLoggedIn={loginUser.isLoggedIn}>
      <div className='layout-tab'>
        <div className='layout-tab__header u-bg-white'>
          <div className='container'>
            <ContentHeader
              screenId={SCREEN.LINKED_DATA_RESULT.ID}
              screenName={SCREEN.LINKED_DATA_RESULT.NAME}
              titleIcon={
                <div className='dashboard-summary-icon-wrap dashboard-summary-icon-wrap--small dashboard-summary-icon-wrap--search u-float-left u-mr8'>
                  <LinkedDataIcon className='dashboard-summary-icon' />
                </div>
              }
              statusType=''
              loadingStatus={fetchLoadingStatus}
              data-testid='contentHeader'>
              <SearchConditionInput
                toggleDrower={() => {
                  setIsOpenDrawer((prev) => !prev);
                }}
                data-testid='searchConditionInput'
                searchBranchCount={0}
              />
            </ContentHeader>
          </div>
        </div>
        <hr className='uc-divider' />
        <div className='layout-tab__content'>
          <div className='container u-flex u-flex-column u-h-full'>
            {fetchLoadingStatus.isLoading ? (
              <div className='u-flex u-h-viewport u-items-center u-justify-center' data-testid='loadingArea'>
                <div className='u-text-center'>
                  <CircularProgress isIndeterminate hasBackground={false} />
                  <p>ページを読み込んでいます...</p>
                </div>
              </div>
            ) : (
              <>
                <SummmaryListComponent
                  items={[
                    {
                      icon: 'phone_iphone',
                      header: 'モバイル',
                      data: summary.mobile,
                    },
                    {
                      icon: 'language',
                      header: 'インターネット・固定電話',
                      data: summary.internet,
                    },
                    {
                      icon: 'lightbulb_outline',
                      header: 'おうちでんき',
                      data: summary.electric,
                    },
                  ]}
                />
                {/* 検索結果エリア */}
                <div className='clearfix u-mb16 u-mt16 u-pl8 u-pr8'>
                  <div className='u-float-left'>
                    <Button
                      label={labelButtonSort(order)}
                      data-testid-e2e='sort-by-create-date'
                      data-testid='sort-by-create-date'
                      type='contained'
                      onClick={handleClickSortOrder}
                    />
                  </div>
                  {searchLinkedDataByPage.length > 0 && (
                    <div className='u-float-right' data-testid-e2e='linkRegistration-paging'>
                      <Pagenation
                        maxNum={totalRecords}
                        existNum={cooperationInfoList.length}
                        pageRows={DEFAULT_PAGE_VIEW.RECORD_ROWS}
                        currentPage={page}
                        setCurrentPage={(pageNum: number) => {
                          dispatch(actions.filterLinkedData.updateCurrentPage(pageNum));
                          scrollToTop(scrollRef);
                        }}
                        scrollSize={DEFAULT_SCROLL_INFO.SIZE}
                        isRight
                        //下記は使わないため初期値で設定
                        apiUpdateIndex={apiUpdateIndex}
                        setApiUpdateIndex={(position: number) => dispatch(actions.filterLinkedData.updateApiIndexValue(position))}
                        search={handleSearchWhenChangeFromValue}
                        //テスト用
                        data-testid='pagenation'
                      />
                    </div>
                  )}
                </div>

                <LinkedDataResultTable
                  scrollRefTable={scrollRef}
                  className='u-ml8 u-mr8'
                  data={searchLinkedDataByPage}
                  tableHeader={
                    <LinkedDataResultTable.Header>
                      <LinkedDataResultTable.Th
                        className='u-bg-background-alt store-name-column'
                        data-testid='store-name-column'
                        data-testid-e2e='store-name-column'>
                        店舗名
                      </LinkedDataResultTable.Th>

                      <LinkedDataResultTable.Th
                        className='u-bg-background-alt reception-date-time-column'
                        data-testid='reception-date-time-column'
                        data-testid-e2e='reception-date-time-column'>
                        受付日時
                      </LinkedDataResultTable.Th>

                      <LinkedDataResultTable.Th
                        className='u-bg-background-alt customer-name-column'
                        data-testid='customer-name-column'
                        data-testid-e2e='customer-name-column'>
                        お客さま番号
                      </LinkedDataResultTable.Th>

                      <LinkedDataResultTable.Th
                        className='u-bg-background-alt business-content-column'
                        data-testid='business-content-column'
                        data-testid-e2e='business-content-column'>
                        業務内容
                      </LinkedDataResultTable.Th>

                      <LinkedDataResultTable.Th
                        className='u-bg-background-alt reception-details-column'
                        data-testid='reception-details-column'
                        data-testid-e2e='reception-details-column'>
                        受付内容
                      </LinkedDataResultTable.Th>

                      <LinkedDataResultTable.Th
                        className='u-bg-background-alt receptionist-column'
                        data-testid='receptionist-column'
                        data-testid-e2e='receptionist-column'>
                        受付担当者
                      </LinkedDataResultTable.Th>
                    </LinkedDataResultTable.Header>
                  }
                  renderTableItem={(item: CooperationInfoList, idx: number) => (
                    <LinkedDataResultTable.Item
                      key={idx}
                      data-testid={`linked-data-result--table-Item-${idx}`}
                      data-testid-e2e='linked-data-result-table-item'>
                      <LinkedDataResultTable.Td className='store-name-column' data-testid={`linked-data-result--table-Td-storeName-${idx}`}>
                        {renderStoreName(item)}
                      </LinkedDataResultTable.Td>

                      <LinkedDataResultTable.Td
                        className='reception-date-time-column'
                        data-testid={`linked-data-result--table-Td-receptionDateTime-${idx}`}>
                        {renderAcceptedDatetime(item.acceptedDatetime)}
                      </LinkedDataResultTable.Td>

                      <LinkedDataResultTable.Td
                        className='customer-name-column'
                        data-testid={`linked-data-result--table-Td-customerNumber-${idx}`}>
                        {renderTextLinkedDataTd(item.customerNumber)}
                      </LinkedDataResultTable.Td>

                      <LinkedDataResultTable.Td
                        className='business-content-column'
                        data-testid={`linked-data-result--table-Td-businessContent-${idx}`}>
                        <>{renderTitleSummary(item.businessType)}</>
                      </LinkedDataResultTable.Td>

                      <LinkedDataResultTable.Td
                        className='reception-details-column'
                        data-testid={`linked-data-result--table-Td-receptionDetails-${idx}`}>
                        <>{renderLabelSummary(item.businessType)}</>
                      </LinkedDataResultTable.Td>

                      <LinkedDataResultTable.Td
                        className='receptionist-column'
                        data-testid={`linked-data-result--table-Td-receptionist-${idx}`}>
                        {!areCrewCodeAndNameMissing(item.registrationCrewCode, item.registrationCrewName) && (
                          <>
                            <div
                              data-testid={`linked-data-result--crewName-${idx}`}
                              data-testid-e2e={`linked-data-result--crewName-${idx}`}>
                              {renderTextLinkedDataTd(item.registrationCrewName)}
                            </div>
                            <div
                              data-testid={`linked-data-result--crewCode-${idx}`}
                              data-testid-e2e={`linked-data-result--crewCode-${idx}`}>{`（${item.registrationCrewCode}）`}</div>
                          </>
                        )}
                      </LinkedDataResultTable.Td>
                    </LinkedDataResultTable.Item>
                  )}
                />
              </>
            )}
          </div>
          <FilterLinkedDataDrawer
            isOpen={isOpenDrawer}
            onClose={handleCloseFilterLinkedDataDrawer}
            search={searchLinkRegistration}
            storeSelectSection={<LinkedDataStoreSelectModal />}
          />
        </div>
      </div>
      <StoreSelectModal />
    </Layout>
  );
};

export default LinkedDataResult;
